import { apiClient } from './api-client';

// ==================== Enums ====================

export enum TriggerType {
  TICKET_CREATED = 'ticket_created',
  TICKET_UPDATED = 'ticket_updated',
  TICKET_STATUS_CHANGED = 'ticket_status_changed',
  TICKET_ASSIGNED = 'ticket_assigned',
  TICKET_PRIORITY_CHANGED = 'ticket_priority_changed',
  TICKET_SLA_BREACH = 'ticket_sla_breach',
  // Reply triggers (the BFF upper-cases to MESSAGE_RECEIVED / MESSAGE_SENT).
  MESSAGE_RECEIVED = 'message_received',
  MESSAGE_SENT = 'message_sent',
  CUSTOMER_CREATED = 'customer_created',
  CUSTOMER_UPDATED = 'customer_updated',
  CHAT_STARTED = 'chat_started',
  CHAT_ENDED = 'chat_ended',
  CHAT_IDLE = 'chat_idle',
  EMAIL_RECEIVED = 'email_received',
  SCHEDULED = 'scheduled',
  MANUAL = 'manual',
  WEBHOOK = 'webhook',
  LINE_ITEM_QUOTE_VALIDATION_FAILED = 'line_item_quote_validation_failed',
}

// Values MUST match the backend Prisma ExecutionStatus enum (uppercase), which
// is what the API stores, returns, and validates status query params against.
// Using lowercase here previously broke status badge colors and made the
// executions status filter return nothing (bug: sent 'completed', DB has 'COMPLETED').
export enum ExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
}

export enum ActionType {
  SET_STATUS = 'set_status',
  SET_PRIORITY = 'set_priority',
  ASSIGN_AGENT = 'assign_agent',
  ASSIGN_TEAM = 'assign_team',
  /** Pick an agent with round robin / least busy / random. */
  AUTO_ASSIGN = 'auto_assign',
  ADD_TAG = 'add_tag',
  REMOVE_TAG = 'remove_tag',
  SEND_EMAIL = 'send_email',
  SEND_NOTIFICATION = 'send_notification',
  TRIGGER_WEBHOOK = 'trigger_webhook',
  TRIGGER_N8N_WORKFLOW = 'trigger_n8n_workflow',
  CREATE_TICKET = 'create_ticket',
  // Branching/Control flow actions
  CONDITIONAL = 'conditional',
  SWITCH = 'switch',
  STOP_WORKFLOW = 'stop_workflow',
}

// ==================== Interfaces ====================

export interface Condition {
  field: string;
  operator: ConditionOperator;
  value?: string | number | boolean | string[];
}

export interface ConditionGroup {
  logic: 'AND' | 'OR';
  conditions: Condition[];
}

// Conditional (if/else) branching config
export interface ConditionalConfig {
  condition: ConditionGroup;
  thenActions: Action[];
  elseActions?: Action[];
}

// Switch/case branching config
export interface SwitchCase {
  value: string | number | boolean;
  actions: Action[];
}

export interface SwitchConfig {
  field: string;
  cases: SwitchCase[];
  defaultActions?: Action[];
}

export interface Action {
  type: ActionType;
  config: Record<string, unknown>;
  delay?: number;
  // For CONDITIONAL action type
  conditional?: ConditionalConfig;
  // For SWITCH action type
  switch?: SwitchConfig;
}

// v2 Workflow Node/Edge definitions for visual builder
export interface WorkflowDefinition {
  version: '2.0';
  entryNodeId: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface WorkflowNode {
  id: string;
  type: string;
  label?: string;
  position?: { x: number; y: number };
  config: Record<string, unknown>;
}

export interface WorkflowEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle?: string;
  label?: string;
}

export interface AutomationRule {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  triggerType: TriggerType;
  triggerConditions: ConditionGroup;
  actions: Action[];
  executionOrder: number;
  stopOnMatch: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // v2 fields
  schemaVersion?: 1 | 2;
  workflow?: WorkflowDefinition;
}

/**
 * Structured error information from a failed execution.
 * Provides detailed, parseable data for debugging and user display.
 */
export interface StructuredExecutionError {
  /** Primary error message for quick display */
  summary: string;
  /** Error code for categorization */
  errorCode?: string;
  /** Which action failed (index in the actions array, 0-based) */
  failedActionIndex?: number;
  /** Type of the failed action */
  failedActionType?: string;
  /** Detailed error information */
  details?: {
    /** Error code for categorization (e.g., 'HTTP_ERROR', 'VALIDATION_ERROR', 'TIMEOUT') */
    errorCode: string;
    /** Human-readable error message */
    message: string;
    /** HTTP status code if applicable */
    httpStatus?: number;
    /** Relevant request/response data for debugging */
    requestInfo?: {
      method?: string;
      url?: string;
      bodyPreview?: string;
    };
    responseInfo?: {
      status?: number;
      bodyPreview?: string;
    };
    /** Stack trace for debugging */
    stackTrace?: string;
    /** Additional context-specific details */
    metadata?: Record<string, unknown>;
  };
  /** Input context at time of failure */
  inputContext?: {
    entityId?: string;
    entityType?: string;
    entitySnapshot?: Record<string, unknown>;
    resolvedVariables?: Record<string, string>;
  };
  /** Timestamp when the failure occurred */
  failedAt: string;
  /** Number of actions that completed before the failure */
  completedActionsCount: number;
  /** Total number of actions in the workflow */
  totalActionsCount: number;
  /** Results from actions that completed before the failure */
  partialResults?: Array<{
    actionType: string;
    actionIndex: number;
    success: boolean;
    duration: number;
  }>;
}

export interface AutomationExecution {
  id: string;
  tenantId: string;
  ruleId: string;
  triggerType: TriggerType;
  triggerPayload: Record<string, unknown>;
  entityType?: string;
  entityId?: string;
  status: ExecutionStatus;
  startedAt?: string;
  completedAt?: string;
  /** Raw error message (may be JSON string for backward compatibility) */
  errorMessage?: string;
  /** Parsed structured error information for detailed failure analysis */
  structuredError?: StructuredExecutionError;
  actionsExecuted?: Record<string, unknown>[];
  createdAt: string;
}

export interface CreateAutomationRuleDto {
  name: string;
  description?: string;
  triggerType: TriggerType;
  triggerConditions: ConditionGroup;
  actions: Action[];
  executionOrder?: number;
  stopOnMatch?: boolean;
  isActive?: boolean;
  // v2 fields
  schemaVersion?: 1 | 2;
  workflow?: WorkflowDefinition;
}

export interface UpdateAutomationRuleDto {
  name?: string;
  description?: string;
  triggerType?: TriggerType;
  triggerConditions?: ConditionGroup;
  actions?: Action[];
  executionOrder?: number;
  stopOnMatch?: boolean;
  isActive?: boolean;
  // v2 fields
  schemaVersion?: 1 | 2;
  workflow?: WorkflowDefinition;
}

export interface AutomationRuleFilter {
  triggerType?: TriggerType;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface ExecutionFilter {
  ruleId?: string;
  status?: ExecutionStatus;
  entityType?: string;
  entityId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedRulesResponse {
  data: AutomationRule[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedExecutionsResponse {
  data: AutomationExecution[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface RuleStats {
  total: number;
  active: number;
}

export interface ExecutionStats {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  running: number;
}

export interface TestRuleResult {
  matched: boolean;
  conditionResults: Array<{
    field: string;
    operator: string;
    expected: unknown;
    actual: unknown;
    result: boolean;
  }>;
}

// ==================== Scheduled Jobs ====================

export enum ScheduledJobType {
  AUTOMATION_RULE = 'automation_rule',
  N8N_WORKFLOW = 'n8n_workflow',
  SLA_CHECK = 'sla_check',
  STALE_TICKET_CHECK = 'stale_ticket_check',
  REPORT_GENERATION = 'report_generation',
  CLEANUP = 'cleanup',
  CUSTOM = 'custom',
}

export interface ScheduledJob {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  cronExpression: string;
  timezone: string;
  jobType: ScheduledJobType;
  jobConfig: Record<string, unknown>;
  lastRunAt?: string;
  nextRunAt?: string;
  lastRunStatus?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduledJobDto {
  name: string;
  description?: string;
  isActive?: boolean;
  cronExpression: string;
  timezone?: string;
  jobType: ScheduledJobType;
  jobConfig: Record<string, unknown>;
}

export interface UpdateScheduledJobDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  cronExpression?: string;
  timezone?: string;
  jobType?: ScheduledJobType;
  jobConfig?: Record<string, unknown>;
}

export interface ScheduledJobFilter {
  jobType?: ScheduledJobType;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedJobsResponse {
  data: ScheduledJob[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface JobStats {
  total: number;
  active: number;
}

// ==================== Service ====================

class WorkflowService {
  // ==================== Automation Rules ====================

  async createRule(dto: CreateAutomationRuleDto): Promise<AutomationRule> {
    const response = await apiClient.post<AutomationRule>('/workflows/rules', dto);
    return response.data;
  }

  async getRules(filter: AutomationRuleFilter = {}): Promise<PaginatedRulesResponse> {
    const params = new URLSearchParams();
    if (filter.triggerType) params.append('triggerType', filter.triggerType);
    if (filter.isActive !== undefined) params.append('isActive', String(filter.isActive));
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedRulesResponse>(
      `/workflows/rules?${params.toString()}`
    );
    return response.data;
  }

  async getRuleById(ruleId: string): Promise<AutomationRule> {
    const response = await apiClient.get<AutomationRule>(`/workflows/rules/${ruleId}`);
    return response.data;
  }

  async getRulesByTrigger(triggerType: TriggerType): Promise<AutomationRule[]> {
    const response = await apiClient.get<AutomationRule[]>(
      `/workflows/rules/by-trigger/${triggerType}`
    );
    return response.data;
  }

  async updateRule(ruleId: string, dto: UpdateAutomationRuleDto): Promise<AutomationRule> {
    const response = await apiClient.put<AutomationRule>(`/workflows/rules/${ruleId}`, dto);
    return response.data;
  }

  async deleteRule(ruleId: string): Promise<void> {
    await apiClient.delete(`/workflows/rules/${ruleId}`);
  }

  async toggleRule(ruleId: string, isActive: boolean): Promise<AutomationRule> {
    const response = await apiClient.post<AutomationRule>(
      `/workflows/rules/${ruleId}/toggle`,
      { isActive }
    );
    return response.data;
  }

  async duplicateRule(ruleId: string, name?: string): Promise<AutomationRule> {
    const response = await apiClient.post<AutomationRule>(
      `/workflows/rules/${ruleId}/duplicate`,
      { name }
    );
    return response.data;
  }

  async getRuleStats(): Promise<RuleStats> {
    const response = await apiClient.get<RuleStats>('/workflows/rules/stats');
    return response.data;
  }

  async testRule(ruleId: string, sampleData?: Record<string, unknown>): Promise<TestRuleResult> {
    const response = await apiClient.post<TestRuleResult>(
      `/workflows/rules/${ruleId}/test`,
      { sampleData },
    );
    return response.data;
  }

  // ==================== Executions ====================

  async getExecutions(filter: ExecutionFilter = {}): Promise<PaginatedExecutionsResponse> {
    const params = new URLSearchParams();
    if (filter.ruleId) params.append('ruleId', filter.ruleId);
    if (filter.status) params.append('status', filter.status);
    if (filter.entityType) params.append('entityType', filter.entityType);
    if (filter.entityId) params.append('entityId', filter.entityId);
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedExecutionsResponse>(
      `/workflows/executions?${params.toString()}`
    );
    return response.data;
  }

  async getExecutionById(executionId: string): Promise<AutomationExecution> {
    const response = await apiClient.get<AutomationExecution>(
      `/workflows/executions/${executionId}`
    );
    return response.data;
  }

  async getExecutionStats(ruleId?: string): Promise<ExecutionStats> {
    const params = ruleId ? `?ruleId=${ruleId}` : '';
    const response = await apiClient.get<ExecutionStats>(`/workflows/executions/stats${params}`);
    return response.data;
  }

  // ==================== Scheduled Jobs ====================

  async createJob(dto: CreateScheduledJobDto): Promise<ScheduledJob> {
    const response = await apiClient.post<ScheduledJob>('/scheduled-jobs', dto);
    return response.data;
  }

  async getJobs(filter: ScheduledJobFilter = {}): Promise<PaginatedJobsResponse> {
    const params = new URLSearchParams();
    if (filter.jobType) params.append('jobType', filter.jobType);
    if (filter.isActive !== undefined) params.append('isActive', String(filter.isActive));
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedJobsResponse>(
      `/scheduled-jobs?${params.toString()}`
    );
    return response.data;
  }

  async getJobById(jobId: string): Promise<ScheduledJob> {
    const response = await apiClient.get<ScheduledJob>(`/scheduled-jobs/${jobId}`);
    return response.data;
  }

  async updateJob(jobId: string, dto: UpdateScheduledJobDto): Promise<ScheduledJob> {
    const response = await apiClient.put<ScheduledJob>(`/scheduled-jobs/${jobId}`, dto);
    return response.data;
  }

  async deleteJob(jobId: string): Promise<void> {
    await apiClient.delete(`/scheduled-jobs/${jobId}`);
  }

  async toggleJob(jobId: string, isActive: boolean): Promise<ScheduledJob> {
    const response = await apiClient.post<ScheduledJob>(
      `/scheduled-jobs/${jobId}/toggle`,
      { isActive }
    );
    return response.data;
  }

  async runJobNow(
    jobId: string
  ): Promise<{ success: boolean; status: string; affected?: number; message?: string }> {
    const response = await apiClient.post<{
      success: boolean;
      status: string;
      affected?: number;
      message?: string;
    }>(`/scheduled-jobs/${jobId}/run-now`, {});
    return response.data;
  }

  async getJobStats(): Promise<JobStats> {
    const response = await apiClient.get<JobStats>('/scheduled-jobs/stats');
    return response.data;
  }

  getJobTypeLabel(type: ScheduledJobType): string {
    const labels: Record<ScheduledJobType, string> = {
      [ScheduledJobType.AUTOMATION_RULE]: 'Automation Rule',
      [ScheduledJobType.N8N_WORKFLOW]: 'n8n Workflow',
      [ScheduledJobType.SLA_CHECK]: 'SLA Check',
      [ScheduledJobType.STALE_TICKET_CHECK]: 'Stale Ticket Check',
      [ScheduledJobType.REPORT_GENERATION]: 'Report Generation',
      [ScheduledJobType.CLEANUP]: 'Cleanup',
      [ScheduledJobType.CUSTOM]: 'Custom Webhook',
    };
    return labels[type] || type;
  }

  // ==================== Helpers ====================

  getTriggerTypeLabel(type: TriggerType): string {
    const labels: Record<TriggerType, string> = {
      [TriggerType.TICKET_CREATED]: 'Ticket Created',
      [TriggerType.TICKET_UPDATED]: 'Ticket Updated',
      [TriggerType.TICKET_STATUS_CHANGED]: 'Ticket Status Changed',
      [TriggerType.TICKET_ASSIGNED]: 'Ticket Assigned',
      [TriggerType.TICKET_PRIORITY_CHANGED]: 'Priority Changed',
      [TriggerType.TICKET_SLA_BREACH]: 'SLA Breach',
      [TriggerType.MESSAGE_RECEIVED]: 'Customer Replies',
      [TriggerType.MESSAGE_SENT]: 'Agent Replies',
      [TriggerType.CUSTOMER_CREATED]: 'Customer Created',
      [TriggerType.CUSTOMER_UPDATED]: 'Customer Updated',
      [TriggerType.CHAT_STARTED]: 'Chat Started',
      [TriggerType.CHAT_ENDED]: 'Chat Ended',
      [TriggerType.CHAT_IDLE]: 'Chat Idle',
      [TriggerType.EMAIL_RECEIVED]: 'Email Received',
      [TriggerType.SCHEDULED]: 'Scheduled',
      [TriggerType.MANUAL]: 'Manual',
      [TriggerType.WEBHOOK]: 'Webhook',
      [TriggerType.LINE_ITEM_QUOTE_VALIDATION_FAILED]: 'Quote Validation Failed',
    };
    // The API returns UPPERCASE trigger types; the labels are keyed lowercase.
    return labels[String(type).toLowerCase() as TriggerType] || type;
  }

  getActionTypeLabel(type: ActionType): string {
    const labels: Record<ActionType, string> = {
      [ActionType.SET_STATUS]: 'Set Status',
      [ActionType.SET_PRIORITY]: 'Set Priority',
      [ActionType.ASSIGN_AGENT]: 'Assign Agent',
      [ActionType.ASSIGN_TEAM]: 'Assign Team',
      [ActionType.AUTO_ASSIGN]: 'Auto-assign Agent',
      [ActionType.ADD_TAG]: 'Add Tag',
      [ActionType.REMOVE_TAG]: 'Remove Tag',
      [ActionType.SEND_EMAIL]: 'Send Email',
      [ActionType.SEND_NOTIFICATION]: 'Send Notification',
      [ActionType.TRIGGER_WEBHOOK]: 'Trigger Webhook',
      [ActionType.TRIGGER_N8N_WORKFLOW]: 'Trigger n8n Workflow',
      [ActionType.CREATE_TICKET]: 'Create Ticket',
      // Branching/Control flow
      [ActionType.CONDITIONAL]: 'If/Else Condition',
      [ActionType.SWITCH]: 'Switch/Case',
      [ActionType.STOP_WORKFLOW]: 'Stop Workflow',
    };
    return labels[type] || type;
  }

  getExecutionStatusColor(status: ExecutionStatus): string {
    const colors: Record<ExecutionStatus, string> = {
      [ExecutionStatus.PENDING]: 'bg-gray-100 text-gray-700',
      [ExecutionStatus.RUNNING]: 'bg-blue-100 text-blue-700',
      [ExecutionStatus.COMPLETED]: 'bg-green-100 text-green-700',
      [ExecutionStatus.FAILED]: 'bg-red-100 text-red-700',
      [ExecutionStatus.SKIPPED]: 'bg-yellow-100 text-yellow-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
